import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { MultiImagePicker } from 'app/design/primitives/multi-image-picker';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { Modal } from 'app/design/primitives/modal';
import type { ImagePickerAsset } from 'expo-image-picker';
import { IconArrowLeft, IconChevronLeft, IconWorld } from '@tabler/icons-react-native';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextInput } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useNavigation } from 'app/hooks/navigation';
import { renderHeaderLeftBack } from 'app/lib/navigation/header-renderers';
import { StoryLoanCard, type StoryLoanContext } from './story-loan-card';

const theme = createDSTheme();

interface StoryPostComposerProps {
  loan: StoryLoanContext;
  onChangeLoan?: () => void;
  backInHeader?: boolean;
  disabled?: boolean;
  text: string;
  pictures: ImagePickerAsset[];
  onTextChange: (text: string) => void;
  onPicturesChange: (pictures: ImagePickerAsset[]) => void;
  showPublicNotice: boolean;
  onDismissPublicNotice: () => void;
}

export function StoryPostComposer({
  loan,
  onChangeLoan,
  backInHeader = false,
  disabled = false,
  text,
  pictures,
  onTextChange,
  onPicturesChange,
}: StoryPostComposerProps) {
  const { t } = useTranslation('posts');
  const input = useRef<TextInput>(null);
  const name = loan.lender?.profile?.firstName || t('storyComposer.neighbour');
  const startStory = (prompt: string) => {
    if (text.trim()) return;
    onTextChange(t(`storyComposer.starters.${prompt}`, { name }));
    input.current?.focus();
  };
  const isNearCharacterLimit = text.length >= 1800;

  const borrowingNavigation = onChangeLoan ? (
    <ChangeBorrowingButton
      onChangeLoan={onChangeLoan}
      backInHeader={backInHeader}
      disabled={disabled}
      text={text}
      pictureCount={pictures.length}
    />
  ) : null;

  return (
    <>
      {backInHeader ? borrowingNavigation : null}
      <Column gap="spacing.general.x4" style={styles.pageContent}>
        {!backInHeader ? borrowingNavigation : null}
        <Column gap="spacing.general.x2">
          <Text fontSize={26} lineHeight={32} fontWeight="700">
            {t('storyComposer.composeTitle')}
          </Text>
          <Text color="text.secondary" fontSize={14} lineHeight={21}>
            {t('storyComposer.composeHint')}
          </Text>
        </Column>

        <StoryLoanCard loan={loan} />

        <Column gap="spacing.general.x3">
          <Row alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="spacing.general.x2">
            <Text fontWeight="700" fontSize={16}>
              {t('storyComposer.storyTitle')}
            </Text>
            <Text fontSize={12} color="text.secondary">
              {t('storyComposer.shortStoryHint')}
            </Text>
          </Row>
          <Column gap="spacing.general.x2" style={styles.captionField}>
            <TextInput
              ref={input}
              testID="story-text"
              accessibilityLabel={t('storyComposer.storyTitle')}
              editable={!disabled}
              maxLength={2000}
              multiline
              onChangeText={onTextChange}
              placeholder={t('storyComposer.textPlaceholder')}
              placeholderTextColor={theme.colors['text.secondary']}
              style={styles.captionInput}
              textAlignVertical="top"
              value={text}
            />
            {isNearCharacterLimit ? (
              <Text fontSize={11} style={styles.captionCount}>
                {text.length}/2000
              </Text>
            ) : null}
          </Column>
          {!text.trim() ? (
            <Column gap="spacing.general.x2">
              <Text color="text.secondary" fontSize={12}>
                {t('storyComposer.starterHint')}
              </Text>
              <Row flexWrap="wrap" gap="spacing.general.x2">
                {['made', 'tried', 'thanks'].map((prompt) => (
                  <Button
                    key={prompt}
                    testID={`story-starter-${prompt}`}
                    variant="secondary"
                    accessibilityRole="button"
                    disabled={disabled}
                    minHeight={36}
                    paddingVertical="spacing.general.x1"
                    paddingHorizontal="spacing.general.x3"
                    onPress={() => startStory(prompt)}
                    style={{ borderRadius: 18 }}
                  >
                    <Text fontSize={12} fontWeight="600">
                      {t(`storyComposer.prompts.${prompt}`)}
                    </Text>
                  </Button>
                ))}
              </Row>
            </Column>
          ) : null}
        </Column>

        <MultiImagePicker
          disabled={disabled}
          variant="compact"
          emptyHint={t('storyComposer.photoHint')}
          images={pictures}
          onChange={onPicturesChange}
          thumbnailSize={88}
          title={t('storyComposer.addPhotos')}
        />

        <Row alignItems="flex-start" gap="spacing.general.x2">
          <IconWorld color={theme.colors['icon.secondary']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
          <Text color="text.secondary" fontSize={12} lineHeight={18} flex={1}>
            {t('storyComposer.publicHint')}
          </Text>
        </Row>
      </Column>
    </>
  );
}

function ChangeBorrowingButton({
  onChangeLoan,
  backInHeader,
  disabled,
  text,
  pictureCount,
}: {
  onChangeLoan: () => void;
  backInHeader: boolean;
  disabled: boolean;
  text: string;
  pictureCount: number;
}) {
  const { t } = useTranslation('posts');
  const navigation = useNavigation<'story-picker'>();
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const keepWriting = () => setShowDiscardWarning(false);
  const changeBorrowing = () => {
    if (disabled) return;
    if (text.trim().length > 0 || pictureCount > 0) setShowDiscardWarning(true);
    else onChangeLoan();
  };
  const discardStory = () => {
    if (disabled) return;
    setShowDiscardWarning(false);
    onChangeLoan();
  };

  useEffect(() => {
    if (!backInHeader) return;
    navigation.setOptions({
      headerLeft: () => (
        <Button
          accessibilityRole="button"
          accessibilityLabel={t('goBack', { ns: 'accessibility' })}
          testID="story-header-back"
          disabled={disabled}
          onPress={() => {
            if (disabled) return;
            if (text.trim() || pictureCount > 0) setShowDiscardWarning(true);
            else onChangeLoan();
          }}
        >
          <IconArrowLeft strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['white-label.header.on-header']} />
        </Button>
      ),
    });
  }, [backInHeader, disabled, navigation, onChangeLoan, pictureCount, t, text]);

  useEffect(() => {
    if (!backInHeader) return;
    return () => navigation.setOptions({ headerLeft: renderHeaderLeftBack });
  }, [backInHeader, navigation]);

  return (
    <>
      {!backInHeader ? (
        <Button
          variant="plain"
          accessibilityRole="button"
          hitSlop={8}
          alignSelf="flex-start"
          onPress={changeBorrowing}
          disabled={disabled}
          testID="story-change-loan"
        >
          <Row alignItems="center" gap="spacing.general.x1" minHeight={28}>
            <IconChevronLeft size={18} color={theme.colors['icon.secondary']} />
            <Text color="text.secondary" fontSize={13}>
              {t('storyComposer.changeMemory')}
            </Text>
          </Row>
        </Button>
      ) : null}
      {showDiscardWarning ? (
        <Modal open transparent onClose={keepWriting}>
          <Modal.Card gap="spacing.general.x4">
            <Modal.Title>{t('storyComposer.discardTitle')}</Modal.Title>
            <Text color="text.secondary" textAlign="center" lineHeight={22}>
              {t('storyComposer.discardMessage')}
            </Text>
            <Column gap="spacing.general.x2">
              <Button variant="primary" onPress={keepWriting} testID="story-keep-writing">
                {t('storyComposer.keepWriting')}
              </Button>
              <Button variant="delete" disabled={disabled} onPress={discardStory} testID="story-discard-draft">
                {t('storyComposer.discardStory')}
              </Button>
            </Column>
          </Modal.Card>
        </Modal>
      ) : null}
    </>
  );
}

export function StoryPostComposerAction({
  disabled,
  loading,
  onPress,
  label,
}: {
  disabled: boolean;
  loading: boolean;
  onPress: () => void;
  label: string;
}) {
  return (
    <Accessory>
      <Column style={styles.actionBar}>
        <Button
          disabled={disabled}
          justifyContent="center"
          loading={loading}
          minHeight={48}
          onPress={onPress}
          style={{
            backgroundColor: disabled ? theme.colors['surface.tertiary-neutral'] : theme.colors['surface.brand.strong'],
            borderRadius: theme.borderRadii['radius.xl'],
          }}
          variant="primary"
        >
          <Text
            color={disabled ? 'text.secondary' : 'surface.tertiary-neutral'}
            fontWeight="700"
            textAlign="center"
            variant="bodyBase"
          >
            {label}
          </Text>
        </Button>
      </Column>
    </Accessory>
  );
}

const styles = StyleSheet.create({
  actionBar: {
    backgroundColor: theme.colors['surface.default'],
    borderTopColor: theme.colors['border.secondary'],
    borderTopWidth: 1,
    paddingHorizontal: theme.spacing['spacing.general.x6'],
    paddingTop: theme.spacing['spacing.general.x3'],
  },
  captionCount: { color: theme.colors['text.secondary'], textAlign: 'right' },
  captionField: {
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: theme.borderRadii['radius.lg'],
    borderWidth: 1,
    padding: theme.spacing['spacing.general.x4'],
  },
  captionInput: { color: theme.colors['text.primary'], fontSize: 16, lineHeight: 24, minHeight: 96, padding: 0 },
  pageContent: { paddingBottom: theme.spacing['spacing.general.x4'] },
});
